import React, { useState, useEffect, useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Outlet, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { ErrorBoundary } from '@/components/shared/ErrorBoundary';
import { AccessDeniedDialog } from '@/components/shared/AccessDeniedDialog';
import { SuspendedPage } from './SuspendedPage';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { ModulesSidebar } from './ModulesSidebar';
import { ClickUpSidebar } from './ClickUpSidebar';
import { OrganizationSidebar } from './OrganizationSidebar';
import { TeamsSidebar } from './TeamsSidebar';
import { ChatSidebar } from '@/modules/chat/ui/ChatSidebar';
import { PlanningSidebar } from './PlanningSidebar';
import { DocumentsSidebar } from './DocumentsSidebar';
import { WikiSidebar } from './WikiSidebar';
// AiSidebar retiré : la page IA possède sa propre sidebar intégrée
import { ProfileSidebar } from './ProfileSidebar';
import { GlobalTopbar } from './GlobalTopbar';
import { MobileNav } from './MobileNav';
import { MobileSidebar } from './MobileSidebar';
import { CommandPalette } from '@/components/CommandPalette';
import { ProductTour, useProductTour } from '@/components/onboarding/ProductTour';
import { Loader2 } from 'lucide-react';
import { useProfileSectionStore } from '@/hooks/useProfileSectionStore';
import { GuestProvider } from '@/contexts/GuestContext';
import { SessionGuards } from '@/components/SessionGuards';

type ActiveModule = 'dashboard' | 'projects' | 'admin' | 'teams' | 'planning' | 'profile' | 'discussion' | 'documents' | 'wiki' | 'ai';

function getActiveModule(pathname: string): ActiveModule {
  if (pathname.startsWith('/dashboard')) {
    return 'dashboard';
  }
  if (pathname.startsWith('/profile')) {
    return 'profile';
  }
  if (pathname.startsWith('/admin') || pathname === '/permissions-guide') {
    return 'admin';
  }
  if (pathname.startsWith('/teams')) {
    return 'teams';
  }
  if (pathname.startsWith('/planning')) {
    return 'planning';
  }
  if (pathname.startsWith('/discussion')) {
    return 'discussion';
  }
  if (pathname.startsWith('/ai')) {
    return 'ai';
  }
  if (pathname.startsWith('/documents')) {
    return 'documents';
  }
  if (pathname.startsWith('/wiki')) {
    return 'wiki';
  }
  return 'projects';
}
import { useAccessControlStore } from '@/modules/core/hooks/useAccessControlStore';
import { useIsMobile } from '@/hooks/use-mobile';
import { getUserRole } from '@/modules/core/data/roleRepository';
import { invalidateOrgAdminsCache } from '@/modules/project/data/permissionRepository';

// Batch 3 (B3.1): polling reduced from 1s to 30s. Realtime on core_member_roles
// + window focus + visibilitychange remain the primary ejection signals; this
// interval is only a safety fallback when realtime is down.
const ADMIN_ACCESS_CHECK_INTERVAL_MS = 30000;

export function AppLayout() {
  const { user, loading: authLoading } = useAuth();
  const { currentOrganization, loading: orgLoading, isSuspended } = useOrganization();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { activeSection, setActiveSection } = useProfileSectionStore();
  const { tourOpen, closeTour } = useProductTour();
  const [accessDeniedDismissed, setAccessDeniedDismissed] = useState(false);
  const roleRefreshTimerRef = React.useRef<number | null>(null);

  // Determine active module
  const activeModule = useMemo(() => getActiveModule(location.pathname), [location.pathname]);

  // IMPORTANT: All hooks must be called before any conditional returns
  const {
    loadCurrentUserAccess,
    loading: accessLoading,
    currentUserRole,
  } = useAccessControlStore(currentOrganization?.id || null);

  const isAdminRoute = location.pathname.startsWith('/admin');
  const canAccessAdmin = currentUserRole === 'owner' || currentUserRole === 'admin';

  // Load access control when org changes
  useEffect(() => {
    if (currentOrganization?.id) {
      loadCurrentUserAccess(currentOrganization.id);
    }
  }, [currentOrganization?.id, loadCurrentUserAccess]);

  // Subscribe to role changes to refresh access control in real-time
  useEffect(() => {
    if (!currentOrganization?.id || !user?.id) return;
    const orgId = currentOrganization.id;

    // Batch 4A (F8): debounce role-change reactions. `core_member_roles` has no
    // org_id column so we can't server-filter by org; instead we collapse bursts
    // (and cross-org noise) into a single invalidate+refresh per 300ms window.
    const scheduleRoleRefresh = () => {
      if (roleRefreshTimerRef.current !== null) {
        window.clearTimeout(roleRefreshTimerRef.current);
      }
      roleRefreshTimerRef.current = window.setTimeout(() => {
        roleRefreshTimerRef.current = null;
        invalidateOrgAdminsCache(orgId);
        void loadCurrentUserAccess(orgId);
      }, 300);
    };

    const channel = supabase
      .channel(`access-control-roles:${orgId}:${user.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'core_member_roles' },
        // Batch 3 (B3.5): invalidate the get_org_admins TTL cache as soon as
        // any role change is observed, so demoted/promoted admins are reflected
        // in permission checks within seconds instead of up to 5 min.
        // Batch 4A (F8): debounced to avoid storms on bulk role mutations.
        scheduleRoleRefresh,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'core_roles', filter: `org_id=eq.${orgId}` },
        scheduleRoleRefresh,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'core_organization_members', filter: `user_id=eq.${user.id}` },
        () => {
          void loadCurrentUserAccess(orgId);
        }
      )
      .subscribe();

    return () => {
      if (roleRefreshTimerRef.current !== null) {
        window.clearTimeout(roleRefreshTimerRef.current);
        roleRefreshTimerRef.current = null;
      }
      supabase.removeChannel(channel);
    };
  }, [currentOrganization?.id, user?.id, loadCurrentUserAccess]);

  // Fallback guard while on admin pages: if realtime delivery is delayed for any reason,
  // verify the role in the background and eject the user immediately.
  useEffect(() => {
    if (!user?.id || !currentOrganization?.id || !isAdminRoute) return;

    let disposed = false;

    const verifyAdminAccess = async () => {
      const role = await getUserRole(currentOrganization.id);
      if (disposed) return;

      if (role !== currentUserRole) {
        void loadCurrentUserAccess(currentOrganization.id);
      }

      if (role && role !== 'owner' && role !== 'admin') {
        navigate('/dashboard', { replace: true });
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        void verifyAdminAccess();
      }
    };

    void verifyAdminAccess();
    const intervalId = window.setInterval(() => {
      void verifyAdminAccess();
    }, ADMIN_ACCESS_CHECK_INTERVAL_MS);

    window.addEventListener('focus', verifyAdminAccess);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      disposed = true;
      window.clearInterval(intervalId);
      window.removeEventListener('focus', verifyAdminAccess);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, currentOrganization?.id, isAdminRoute, currentUserRole, loadCurrentUserAccess, navigate]);

  // Close mobile sidebar on route change
  useEffect(() => {
    setMobileSidebarOpen(false);
    setAccessDeniedDismissed(false);
  }, [location.pathname]);

  // Reset dismissed state when role changes (e.g., demoted in real-time)
  useEffect(() => {
    setAccessDeniedDismissed(false);
  }, [currentUserRole]);

  // Loading state
  if (authLoading || orgLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Auth check
  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // Suspended check - show suspension page
  if (isSuspended) {
    return <SuspendedPage />;
  }

  // Org check
  if (!currentOrganization) {
    return <Navigate to="/onboarding" replace />;
  }

  const isGuest = currentUserRole === 'guest';

  // Hard redirect to dashboard as soon as the user loses admin access while on an admin route.
  if (isAdminRoute && !accessLoading && currentUserRole && !canAccessAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const showAccessDenied = isAdminRoute && !accessLoading && !canAccessAdmin && !accessDeniedDismissed;

  // Render the appropriate module sidebar based on active module
  const renderModuleSidebar = () => {
    if (activeModule === 'profile') {
      return (
        <ProfileSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          activeSection={activeSection}
          onSectionChange={setActiveSection}
        />
      );
    }
    if (activeModule === 'admin') {
      return (
        <OrganizationSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      );
    }
    if (activeModule === 'teams') {
      return (
        <TeamsSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      );
    }
    if (activeModule === 'planning') {
      return (
        <PlanningSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      );
    }
    if (activeModule === 'discussion') {
      return (
        <ChatSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      );
    }
    if (activeModule === 'documents') {
      return (
        <DocumentsSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      );
    }
    if (activeModule === 'wiki') {
      return (
        <WikiSidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
      );
    }
    if (activeModule === 'ai') {
      // La page IA gère sa propre sidebar (conversations réelles)
      return null;
    }
    return (
      <ClickUpSidebar
        collapsed={sidebarCollapsed}
        onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
        userRole={currentUserRole}
      />
    );
  };

  return (
    <GuestProvider isGuest={isGuest}>
      <div className="flex flex-col h-screen w-screen overflow-hidden bg-white gap-2 p-2">
        <CommandPalette />
        <SessionGuards />
        <ProductTour forceOpen={tourOpen} onClose={closeTour} />
        <AccessDeniedDialog
          open={showAccessDenied}
          onOpenChange={(nextOpen) => {
            if (!nextOpen) setAccessDeniedDismissed(true);
          }}
        />

        <GlobalTopbar />

        <div className="flex flex-1 overflow-hidden gap-2">
          <div className="hidden md:block shrink-0">
            <ModulesSidebar />
          </div>

          {activeModule !== 'dashboard' && (
            <div className="hidden md:flex shrink-0">
              {renderModuleSidebar()}
            </div>
          )}

          <main className="flex-1 overflow-hidden flex flex-col pb-16 md:pb-0 bg-white rounded-xl border min-w-0">
            <ErrorBoundary>
              <Outlet />
            </ErrorBoundary>
          </main>
        </div>

        {isMobile && (
          <>
            <MobileNav onOpenSidebar={() => setMobileSidebarOpen(true)} />
            <MobileSidebar
              open={mobileSidebarOpen}
              onOpenChange={setMobileSidebarOpen}
            />
          </>
        )}
      </div>
    </GuestProvider>
  );
}
